import { seasons, groups, suppliers, contactPersons, brandOptions } from './stylesFixtureData';
import type { StyleRow } from './stylesFixtureData';

/**
 * Dedicated, real persisted `stylesData` fixtures for the dl-58403 "Audit logs" automation split,
 * epic DL-65917 ("bulk edit trigger side, what the client sends", Testmo folder 93152 part 1 of
 * 6). Real rows (not an `apiTransform`-only override) so the bulk `PUT /api/styles` these specs
 * exercise round-trips through the same find-by-id/merge/persist/audit-log-append path as every
 * other row — the epic's own assertions are about the outgoing request AND the audit-log entries
 * that path appends, not a canned response. Mirrors `stylesBulkActionsFixtureData.ts` (DL-63757):
 * one dedicated row per case, searchable by a unique name substring absent from every other
 * fixture in this mock.
 *
 * Ids 9530x — checked against every other fixture file's id ranges (9500x/9501x, 95005-95010,
 * 95201-95202) to avoid the `stylesData.findIndex` id-collision failure mode documented on
 * `buildBulkActionsEditFixtureStyles`.
 */

const baseRow = (id: number, name: string, overrides: Partial<StyleRow> = {}): StyleRow => ({
  id,
  name,
  customId: `ST-${id}`,
  description: '',
  state: 'unpublished',
  logo: '',
  season: seasons[2], // SS25 — AW26 (seasons[1]) is a genuine change; AW25 (seasons[0]) is not
  //                     selectable in the bulk-edit Season combo (documented in sibling specs).
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false,
  ...overrides,
});

/**
 * All three rows rely on `baseRow`'s default supplier (`suppliers[0]`, "Ningbo garment
 * supplier") being real and non-null — every story here bulk-edits the Supplier field
 * (`styleSupplier`/`fieldTransforms.supplier`), the only field in Styles' `bulkEditCols`
 * (`config-cols.tsx`) that is BOTH a plain single-value dropdown (simplest combo interaction)
 * AND genuinely `bulkEditable: true`. (`description` is `type: 'text'` but carries no
 * `bulkEditable: true` — verified against the modal's actual rendered fields, not assumed from
 * its presence in `bulkEditCols` — so it never appears in the BulkEditModal at all; every other
 * bulk-editable field is a `dd`/`tags-dd`/`multiselect` combo.)
 */

/** DL-65918 (Testmo 754274) — the anchor case: bulk-edit one field, apply, and read the entry back
 * via the mock-only audit-log endpoint. */
export const buildAuditTriggerAnchorFixtureStyles = (): StyleRow[] => [
  baseRow(95301, 'AuditTriggerFixture Anchor A'),
];

/** DL-65921 (Testmo 754282) — no-op vs. falsy-but-real-change (clearing Supplier sends
 * `SupplierId: 0` explicitly — a real value, not an omitted key). */
export const buildAuditTriggerNoOpFixtureStyles = (): StyleRow[] => [
  baseRow(95302, 'AuditTriggerFixture NoOp A'),
];

/** DL-65925 (Testmo 754298) — a header-only edit must omit `CustomFields` entirely, not send it
 * as `null`/`[]`. Plain row; the case never touches custom fields at all. */
export const buildAuditTriggerHeaderOnlyFixtureStyles = (): StyleRow[] => [
  baseRow(95303, 'AuditTriggerFixture HeaderOnly A'),
];

/** DL-65919 (Testmo 754275) — two header fields (Season + Supplier contact) changed in one modal
 * must go out in a SINGLE `PUT /api/styles` with both fields on the one entry (one row, not two
 * requests). Seeded SS25 (`baseRow`'s default) so a change to AW26 is genuine. */
export const buildAuditTriggerMultiFieldFixtureStyles = (): StyleRow[] => [
  baseRow(95304, 'AuditTriggerFixture MultiField A'),
];

/** DL-65922 (Testmo 754285) — five styles selected → all five ids in the payload, and each
 * style's own audit trail gains the entry (no style left out, none duplicated). Five dedicated
 * rows sharing the unique "AuditTriggerFixture FiveSel" prefix so a search narrows to exactly
 * these five. Season edited to AW26 (all seeded SS25). */
export const buildAuditTriggerFiveSelectionFixtureStyles = (): StyleRow[] =>
  ['A', 'B', 'C', 'D', 'E'].map((suffix, i) =>
    baseRow(95310 + i, `AuditTriggerFixture FiveSel ${suffix}`)
  );

/** DL-65923 (Testmo 754290) — selection is id-keyed and survives a table-state change (a further
 * search/filter): only the genuinely-selected style's id is sent, with no leakage from the
 * adjacent, unselected row that shares the search prefix. Two rows; one gets selected, the other
 * is the adjacent control that must NOT appear in the payload. */
export const buildAuditTriggerIdentityFixtureStyles = (): StyleRow[] => [
  baseRow(95320, 'AuditTriggerFixture Identity Alpha'),
  baseRow(95321, 'AuditTriggerFixture Identity Beta'),
];

/** DL-65924 (Testmo 754293) — the failure/rollback half: a genuine change whose bulk PUT is forced
 * to 500 must surface a readable error, leave the confirm modal open for retry, persist NOTHING
 * (a refetch still shows the original value) and append NO audit entry. */
export const buildAuditTriggerValidationFixtureStyles = (): StyleRow[] => [
  baseRow(95330, 'AuditTriggerFixture Validation A'),
];

/** DL-65927 (Testmo 755408) — scale: 20 styles selected at once must still go out as ONE PUT
 * carrying all 20 ids, with no double-submit and no partial/duplicated audit entries. Twenty
 * dedicated rows sharing the unique "AuditTriggerFixture Scale" prefix; two-digit suffixes keep
 * the search substring unambiguous (01..20). pageSize is 100, so all 20 land on one page and the
 * header select-all checkbox selects the whole result set. */
export const AUDIT_TRIGGER_SCALE_COUNT = 20;
export const buildAuditTriggerScaleFixtureStyles = (): StyleRow[] =>
  Array.from({ length: AUDIT_TRIGGER_SCALE_COUNT }, (_, i) =>
    baseRow(95340 + i, `AuditTriggerFixture Scale ${String(i + 1).padStart(2, '0')}`)
  );

/**
 * DL-65920 (Testmo 754276) — the custom-field bulk-edit payload-shape case: two styles sharing a
 * custom-field value are bulk-edited and the outgoing `PUT /api/styles` must build a correct
 * `CustomFields` section (id-keyed, type-correct value, the KEEP_CURRENT/CLEAR sentinels, no entry
 * for untouched fields). Ids 95305/95306 (the 95305-95309 gap between the MultiField (95304) and
 * FiveSel (95310+) bands — verified free against every builder in this file). Both rows are
 * seeded IDENTICALLY with three unscoped, always-visible CFs so the modal exposes them for every
 * selected style with no group/category-scope setup needed:
 *  - 703 "Fit Type" (single-select AllowedValue) = Regular (7032) — the field changed to Loose,
 *    so its wire entry carries the new allowed-value id (the type-correct form, not the label);
 *  - 708 "Target Weight (g)" (Numeric) = 250 — cleared via "Set empty" to prove the CLEAR sentinel
 *    (key present, value nulled), distinct from an untouched field;
 *  - 701 "Fabric Notes" (Text) = seeded — left untouched to prove KEEP_CURRENT (key absent).
 * Season is seeded SS25 (`baseRow` default) so a change to AW26 is a genuine header positive
 * control riding in the same request as the CF changes.
 */
export const buildAuditTriggerCustomFieldFixtureStyles = (): StyleRow[] =>
  ['A', 'B'].map((suffix, i) =>
    baseRow(95305 + i, `AuditTriggerFixture CustomField ${suffix}`, {
      customFields: [
        { id: 701, type: 'text', value: { id: 0, value: 'Machine wash only' } },
        { id: 703, type: 'allowedvalue', value: { id: 7032, value: 'Regular' } },
        { id: 708, type: 'numeric', value: { id: 0, value: '250' } },
      ],
    })
  );
